// NewArrivalsSection.js
import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/autoplay";
import "./NewArrivalsSection.css";

const NewArrivalsSection = ({ categoryId }) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [activeCart, setActiveCart] = useState({});
  const [inputValues, setInputValues] = useState({});
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch discount percentage
  useEffect(() => {
    const fetchDiscount = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}home.php`);
        if (response.data && response.data.body) {
          const discountValue = response.data.body.discount_value?.[0]?.discount_value || 0;
          setDiscountPercentage(parseFloat(discountValue));
        }
      } catch (error) {
        console.error("Error fetching discount:", error);
      }
    };
    fetchDiscount();
  }, [API_BASE_URL]);

  useEffect(() => {
    const fetchNewArrivals = async () => {
      if (!categoryId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await axios.post(`${API_BASE_URL}product_list.php`, {
          // category_id: categoryId
        });

        if (response.data && response.data.head && response.data.head.code === 200) {
          const newArrivals = response.data.body?.filter(
            product => product.new_arrival === "1"
          ) || [];
          setProducts(newArrivals);
          
          const initialQuantities = {};
          const initialActive = {};
          const initialInputValues = {};
          const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
          
          newArrivals.forEach(product => {
            const existing = cartItems.find(item => item.product_id === product.product_id);
            initialQuantities[product.product_id] = existing ? existing.count : 1;
            initialActive[product.product_id] = existing ? true : false;
            initialInputValues[product.product_id] = existing ? existing.count.toString() : '';
          });
          setQuantities(initialQuantities);
          setActiveCart(initialActive);
          setInputValues(initialInputValues);
        } else {
          console.error("Failed to fetch products:", response.data);
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching new arrivals:", error);
        setError("Failed to load new arrivals");
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNewArrivals();
  }, [categoryId, API_BASE_URL]);

  // Sync with localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
      const newActive = {};
      const newQuantities = {};
      const newInputValues = {};
      products.forEach(product => {
        const existing = cartItems.find(item => item.product_id === product.product_id);
        newActive[product.product_id] = existing ? true : false;
        newQuantities[product.product_id] = existing ? existing.count : 1;
        newInputValues[product.product_id] = existing ? existing.count.toString() : '';
      });
      setActiveCart(newActive);
      setQuantities(newQuantities);
      setInputValues(newInputValues);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("cartUpdated", handleStorageChange);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cartUpdated", handleStorageChange);
    };
  }, [products]);

  // Handle quantity change - like Shop page
  const handleQuantityChange = (productId, value) => {
    setInputValues(prev => ({ ...prev, [productId]: value }));
    
    if (value === '') {
      return;
    }
    
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setQuantities(prev => ({ ...prev, [productId]: numValue }));
      updateCartQuantity(productId, numValue);
    }
  };

  // Handle blur - when user leaves the input
  const handleQuantityBlur = (productId, value) => {
    if (value === '' || isNaN(parseInt(value, 10)) || parseInt(value, 10) < 1) {
      removeFromCart(productId);
      setQuantities(prev => ({ ...prev, [productId]: 1 }));
      setActiveCart(prev => ({ ...prev, [productId]: false }));
      setInputValues(prev => ({ ...prev, [productId]: '' }));
    }
  };

  // Remove from cart
  const removeFromCart = (productId) => {
    let cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    cartItems = cartItems.filter(item => item.product_id !== productId);
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    localStorage.removeItem(`count_${productId}`);
    
    // FIXED: cartCount should be number of UNIQUE products
    const uniqueProductCount = cartItems.length;
    localStorage.setItem("cartCount", uniqueProductCount);
    
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
  };

  // Update cart quantity
  const updateCartQuantity = (productId, newQuantity) => {
    let cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const existingIndex = cartItems.findIndex(item => item.product_id === productId);
    
    if (existingIndex !== -1) {
      cartItems[existingIndex].count = newQuantity;
      localStorage.setItem(`count_${productId}`, newQuantity);
    }
    
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    
    // FIXED: cartCount should be number of UNIQUE products
    const uniqueProductCount = cartItems.length;
    localStorage.setItem("cartCount", uniqueProductCount);
    
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
  };

  // Handle add to cart (first time)
  const handleAddToCart = (product) => {
    const quantity = 1;
    
    const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const existingIndex = cartItems.findIndex(item => item.product_id === product.product_id);
    
    if (existingIndex !== -1) {
      return;
    }
    
    const mrp = parseFloat(product.mrp_rate) || 0;
    const discountAmount = (discountPercentage * mrp) / 100;
    const discountedPrice = mrp - discountAmount;
    
    cartItems.push({
      product_id: product.product_id,
      product_name: product.product_name,
      image: product.product_image,
      oldprice: mrp.toString(),
      price: discountedPrice.toString(),
      count: quantity,
      is_non_discount: "0"
    });
    
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    localStorage.setItem(`count_${product.product_id}`, quantity);
    
    // FIXED: cartCount should be number of UNIQUE products
    const uniqueProductCount = cartItems.length;
    localStorage.setItem("cartCount", uniqueProductCount);
    
    setActiveCart(prev => ({ ...prev, [product.product_id]: true }));
    setQuantities(prev => ({ ...prev, [product.product_id]: quantity }));
    setInputValues(prev => ({ ...prev, [product.product_id]: quantity.toString() }));
    
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
  };

  // Navigate to product detail
  const handleProductClick = (product) => {
    navigate(`/productdetail/${product.product_id}`);
  };

  // Get price with discount
  const getPriceWithDiscount = (mrpRate) => {
    const mrp = parseFloat(mrpRate) || 0;
    const discountAmount = (discountPercentage * mrp) / 100;
    const discountedPrice = mrp - discountAmount;
    
    return {
      original: Math.round(mrp * 100) / 100,
      discounted: Math.round(discountedPrice * 100) / 100,
      hasDiscount: discountPercentage > 0
    };
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    return image;
  };

  if (isLoading) {
    return (
      <div className="new-arrivals-section">
        <div className="new-arrivals-container">
          <div className="new-arrivals-header">
            <div className="new-arrivals-badge">
              <span className="new-arrivals-badge-icon">✨</span>
              <span>New Arrivals</span>
            </div>
            <h2 className="new-arrivals-title">
              Loading Fresh
              <span className="new-arrivals-title-gradient"> Collection</span>
            </h2>
          </div>
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
            Loading new arrivals...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="new-arrivals-section">
        <div className="new-arrivals-container">
          <div className="new-arrivals-header">
            <div className="new-arrivals-badge">
              <span className="new-arrivals-badge-icon">✨</span>
              <span>New Arrivals</span>
            </div>
            <h2 className="new-arrivals-title">
              Fresh
              <span className="new-arrivals-title-gradient"> Collection</span>
            </h2>
          </div>
          <div className="new-arrivals-empty">
            <div className="new-arrivals-empty-icon">⚠️</div>
            <div className="new-arrivals-empty-text">Unable to load new arrivals</div>
            <div className="new-arrivals-empty-subtext">Please try again later</div>
          </div>
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="new-arrivals-section">
        <div className="new-arrivals-container">
          <div className="new-arrivals-header">
            <div className="new-arrivals-badge">
              <span className="new-arrivals-badge-icon">✨</span>
              <span>New Arrivals</span>
            </div>
            <h2 className="new-arrivals-title">
              Fresh
              <span className="new-arrivals-title-gradient"> Collection</span>
            </h2>
            <p className="new-arrivals-subtitle">
              Check back soon for our latest products
            </p>
          </div>
          <div className="new-arrivals-empty">
            <div className="new-arrivals-empty-icon">🎉</div>
            <div className="new-arrivals-empty-text">No new arrivals yet</div>
            <div className="new-arrivals-empty-subtext">Stay tuned for exciting new products!</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="new-arrivals-section">
      <div className="new-arrivals-bg-glow"></div>
      <div className="new-arrivals-bg-grid"></div>

      <div className="new-arrivals-container">
        <div className="new-arrivals-header">
          <div className="new-arrivals-badge">
            <span className="new-arrivals-badge-icon">✨</span>
            <span>New Arrivals</span>
          </div>
          <h2 className="new-arrivals-title">
            Fresh
            <span className="new-arrivals-title-gradient"> Collection</span>
          </h2>
          <p className="new-arrivals-subtitle">
            Discover our latest products - freshly added to our collection
          </p>
        </div>

        <div className="new-arrivals-swiper-wrapper">
          <Swiper
            slidesPerView="auto"
            spaceBetween={24}
            loop={true}
            autoplay={{ delay: 2500, disableOnInteraction: false }}
            modules={[Autoplay]}
            className="new-arrivals-swiper"
          >
            {products.map((product, idx) => {
              const priceInfo = getPriceWithDiscount(product.mrp_rate);
              const inputValue = inputValues[product.product_id] || '';
              const isActive = activeCart[product.product_id] || false;
              
              return (
                <SwiperSlide key={product.product_id} className="new-arrivals-slide">
                  <div className="new-arrival-card" style={{ transitionDelay: `${idx * 50}ms` }}>
                    <div className="new-arrival-card-inner">
                      <div className="new-arrival-image" onClick={() => handleProductClick(product)}>
                        <div className="new-arrival-badge">✨ NEW</div>
                        <img
                          src={getImageUrl(product.product_image)}
                          alt={product.product_name}
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = `${API_BASE_URL}uploads/noimages.jpg`;
                          }}
                        />
                        <div className="new-arrival-image-overlay"></div>
                        <div className="new-arrival-shine"></div>
                        
                        <div className="new-arrival-info-overlay">
                          <h5 className="new-arrival-product-name">
                            {product.product_name}
                          </h5>
                          <div className="new-arrival-price-container">
                            {priceInfo.hasDiscount ? (
                              <>
                                <del className="new-arrival-mrp-price">
                                  ₹{priceInfo.original.toFixed(2)}
                                </del>
                                <span className="new-arrival-online-price">
                                  ₹{priceInfo.discounted.toFixed(2)}
                                </span>
                              </>
                            ) : (
                              <span className="new-arrival-online-price">
                                ₹{priceInfo.original.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Cart Section - Like Shop page */}
                      <div className="new-arrival-cart-section">
                        {!isActive ? (
                          <button 
                            className="new-arrival-add-btn"
                            onClick={() => handleAddToCart(product)}
                          >
                            <i className="bi bi-cart-plus"></i>
                            Add to Cart
                          </button>
                        ) : (
                          <div className="new-arrival-qty-control">
                            <button 
                              className="new-arrival-qty-btn"
                              onClick={() => {
                                const currentQty = parseInt(inputValue) || 1;
                                const newQty = currentQty - 1;
                                if (newQty <= 0) {
                                  removeFromCart(product.product_id);
                                  setActiveCart(prev => ({ ...prev, [product.product_id]: false }));
                                  setInputValues(prev => ({ ...prev, [product.product_id]: '' }));
                                  setQuantities(prev => ({ ...prev, [product.product_id]: 1 }));
                                } else {
                                  setInputValues(prev => ({ ...prev, [product.product_id]: newQty.toString() }));
                                  setQuantities(prev => ({ ...prev, [product.product_id]: newQty }));
                                  updateCartQuantity(product.product_id, newQty);
                                }
                              }}
                            >
                              <b>-</b>
                            </button>
                            <input
                              type="number"
                              className="new-arrival-qty-input"
                              value={inputValue}
                              onChange={(e) => handleQuantityChange(product.product_id, e.target.value)}
                              onBlur={(e) => handleQuantityBlur(product.product_id, e.target.value)}
                              min="0"
                              step="1"
                              placeholder="0"
                            />
                            <button 
                              className="new-arrival-qty-btn"
                              onClick={() => {
                                const currentQty = parseInt(inputValue) || 0;
                                const newQty = currentQty + 1;
                                setInputValues(prev => ({ ...prev, [product.product_id]: newQty.toString() }));
                                setQuantities(prev => ({ ...prev, [product.product_id]: newQty }));
                                updateCartQuantity(product.product_id, newQty);
                              }}
                            >
                              <b>+</b>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </div>
  );
};

export default NewArrivalsSection;